import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Settings as SettingsIcon,
  Mail,
  Key,
  Bell,
  Shield,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Eye,
  EyeOff,
  Send,
  Users,
  X,
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  Check,
  Bold,
  Italic,
  Underline,
  Link,
  List,
  Heading2,
  Code,
  Sparkles,
} from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { useAdminAuth } from '../context/AdminAuthContext';
import ConfirmDialog from '../components/ui/ConfirmDialog';

const TABS = [
  { id: 'email', label: 'Email Settings', icon: Mail },
  { id: 'compose', label: 'Compose Email', icon: Send },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Shield },
];

const DEFAULT_EMAIL_CONFIG = {
  name: '',
  smtpHost: '',
  smtpPort: '587',
  smtpUser: '',
  smtpPassword: '',
  fromName: 'Botibonga',
  fromEmail: '',
};

export default function Settings() {
  const { admin } = useAdminAuth();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const toParam = searchParams.get('to');
  const subjectParam = searchParams.get('subject');

  const [activeTab, setActiveTab] = useState(tabParam || 'email');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Email Configs (multiple)
  const [emailConfigs, setEmailConfigs] = useState([]);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [configForm, setConfigForm] = useState(DEFAULT_EMAIL_CONFIG);
  const [showPassword, setShowPassword] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [configDropdownOpen, setConfigDropdownOpen] = useState(false);

  // Email Composer
  const [emailForm, setEmailForm] = useState({
    configId: '',
    recipients: toParam ? 'custom' : 'all',
    selectedUsers: [],
    customEmails: toParam || '',
    subject: subjectParam || '',
    message: '',
  });
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [isAiLoadingSubject, setIsAiLoadingSubject] = useState(false);
  const [isAiLoadingMessage, setIsAiLoadingMessage] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Notification Settings
  const [notifications, setNotifications] = useState({
    newUser: true,
    newFeedback: true,
    newRequest: true,
    dailyDigest: false,
    weeklyReport: true,
  });

  // Update form when URL params change
  useEffect(() => {
    if (tabParam) {
      setActiveTab(tabParam);
    }
    if (toParam) {
      setEmailForm(prev => ({
        ...prev,
        recipients: 'custom',
        customEmails: toParam,
        subject: subjectParam || prev.subject,
      }));
    }
  }, [tabParam, toParam, subjectParam]);

  useEffect(() => {
    fetchSettings();
    if (activeTab === 'compose') {
      fetchUsers();
    }
  }, [activeTab]);

  // Auto-select first email config if not selected
  useEffect(() => {
    if (emailConfigs.length > 0 && !emailForm.configId) {
      setEmailForm(prev => ({ ...prev, configId: emailConfigs[0].id }));
    }
  }, [emailConfigs, emailForm.configId]);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const response = await adminApi.getSettings();
      if (response.emailConfigs) {
        setEmailConfigs(response.emailConfigs);
      }
      if (response.notifications) {
        setNotifications((prev) => ({ ...prev, ...response.notifications }));
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoadingUsers(true);
      const response = await adminApi.getUsers({ limit: 1000 });
      setUsers(response.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleAddConfig = () => {
    setEditingConfig(null);
    setConfigForm(DEFAULT_EMAIL_CONFIG);
    setShowConfigForm(true);
    setShowPassword(false);
  };

  const handleEditConfig = (config) => {
    setEditingConfig(config);
    setConfigForm({
      name: config.name || '',
      smtpHost: config.smtpHost || '',
      smtpPort: config.smtpPort || '587',
      smtpUser: config.smtpUser || '',
      smtpPassword: '', // Always clear - leave blank to keep current password
      fromName: config.fromName || 'Botibonga',
      fromEmail: config.fromEmail || '',
    });
    setShowConfigForm(true);
    setShowPassword(false);
  };

  const handleSaveConfig = async () => {
    if (!configForm.name.trim()) {
      setError('Please enter a name for this email configuration');
      return;
    }
    if (!configForm.smtpHost.trim() || !configForm.smtpUser.trim()) {
      setError('SMTP Host and Username are required');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      if (editingConfig) {
        await adminApi.updateEmailConfig(editingConfig.id, configForm);
        setEmailConfigs(prev => prev.map(c =>
          c.id === editingConfig.id ? { ...c, ...configForm, smtpPassword: '********' } : c
        ));
        setSuccessMessage('Email configuration updated');
      } else {
        const result = await adminApi.createEmailConfig(configForm);
        setEmailConfigs(prev => [...prev, result.emailConfig]);
        setSuccessMessage('Email configuration created');
      }

      setShowConfigForm(false);
      setConfigForm(DEFAULT_EMAIL_CONFIG);
      setEditingConfig(null);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save email configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfig = async () => {
    if (!deleteConfirm) return;

    try {
      setIsDeleting(true);
      await adminApi.deleteEmailConfig(deleteConfirm.id);
      setEmailConfigs(prev => prev.filter(c => c.id !== deleteConfirm.id));

      // Clear selection if deleted config was selected
      if (emailForm.configId === deleteConfirm.id) {
        setEmailForm(prev => ({ ...prev, configId: '' }));
      }

      setDeleteConfirm(null);
      setSuccessMessage('Email configuration deleted');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete email configuration');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveNotifications = async () => {
    try {
      setIsSaving(true);
      setError(null);
      await adminApi.updateSettings({ notifications });
      setSuccessMessage('Notification settings saved');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to save notification settings');
    } finally {
      setIsSaving(false);
    }
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSendEmail = async () => {
    if (!emailForm.configId) {
      setError('Please select an email configuration to send from. You can add one in the Email Settings tab.');
      return;
    }

    if (!emailForm.subject.trim() || !emailForm.message.trim()) {
      setError('Subject and message are required');
      return;
    }

    let recipients = [];
    if (emailForm.recipients === 'all') {
      recipients = users.map((u) => u.email);
      if (recipients.length === 0) {
        setError('No users available to send emails to.');
        return;
      }
    } else if (emailForm.recipients === 'selected') {
      recipients = emailForm.selectedUsers;
      if (recipients.length === 0) {
        setError('Please select at least one user from the list.');
        return;
      }
    } else {
      const customList = emailForm.customEmails.split(',').map((e) => e.trim()).filter(Boolean);
      if (customList.length === 0) {
        setError('Please enter at least one email address.');
        return;
      }
      const invalidEmails = customList.filter((e) => !isValidEmail(e));
      if (invalidEmails.length > 0) {
        setError(`Invalid email address${invalidEmails.length > 1 ? 'es' : ''}: ${invalidEmails.join(', ')}`);
        return;
      }
      recipients = customList;
    }

    try {
      setIsSending(true);
      setError(null);
      setSendResult(null);
      const result = await adminApi.sendBulkEmail({
        configId: emailForm.configId,
        recipients,
        recipientType: emailForm.recipients,
        subject: emailForm.subject,
        message: emailForm.message,
      });
      setSendResult(result);
      setEmailForm(prev => ({
        ...prev,
        recipients: 'all',
        selectedUsers: [],
        customEmails: '',
        subject: '',
        message: '',
      }));
      setTimeout(() => setSendResult(null), 5000);
    } catch (err) {
      setError(err.message || 'Failed to send email. Please check your email settings.');
    } finally {
      setIsSending(false);
    }
  };

  const toggleUserSelection = (email) => {
    setEmailForm((prev) => ({
      ...prev,
      selectedUsers: prev.selectedUsers.includes(email)
        ? prev.selectedUsers.filter((e) => e !== email)
        : [...prev.selectedUsers, email],
    }));
  };

  const selectedConfig = emailConfigs.find(c => c.id === emailForm.configId);
  const messageRef = useRef(null);

  const insertFormatting = (openTag, closeTag) => {
    const textarea = messageRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = emailForm.message;
    const selectedText = text.substring(start, end);

    let newText;
    let newCursorPos;

    if (selectedText) {
      newText = text.substring(0, start) + openTag + selectedText + closeTag + text.substring(end);
      newCursorPos = end + openTag.length + closeTag.length;
    } else {
      newText = text.substring(0, start) + openTag + closeTag + text.substring(end);
      newCursorPos = start + openTag.length;
    }

    setEmailForm({ ...emailForm, message: newText });

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (!url) return;

    const textarea = messageRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = emailForm.message;
    const selectedText = text.substring(start, end) || 'Link text';

    const linkHtml = `<a href="${url}" style="color: #6366f1; text-decoration: underline;">${selectedText}</a>`;
    const newText = text.substring(0, start) + linkHtml + text.substring(end);

    setEmailForm({ ...emailForm, message: newText });

    setTimeout(() => {
      textarea.focus();
    }, 0);
  };

  const insertList = () => {
    const textarea = messageRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const text = emailForm.message;

    const listHtml = '\n<ul style="margin: 10px 0; padding-left: 20px;">\n  <li>Item 1</li>\n  <li>Item 2</li>\n  <li>Item 3</li>\n</ul>\n';
    const newText = text.substring(0, start) + listHtml + text.substring(start);

    setEmailForm({ ...emailForm, message: newText });

    setTimeout(() => {
      textarea.focus();
    }, 0);
  };

  const insertHeading = () => {
    const textarea = messageRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = emailForm.message;
    const selectedText = text.substring(start, end) || 'Heading';

    const headingHtml = `<h2 style="margin: 16px 0 8px 0; font-size: 18px; font-weight: 600; color: #1a1a1a;">${selectedText}</h2>`;
    const newText = text.substring(0, start) + headingHtml + text.substring(end);

    setEmailForm({ ...emailForm, message: newText });

    setTimeout(() => {
      textarea.focus();
    }, 0);
  };

  const renderHtmlPreview = () => {
    // Check if message contains HTML tags
    const containsHtml = /<[^>]+>/.test(emailForm.message);

    let htmlContent = emailForm.message
      .replace(/\{username\}/g, '<span style="color: #6366f1; font-weight: 500;">[User Name]</span>');

    // Only convert newlines to <br> if content doesn't contain HTML
    if (!containsHtml) {
      htmlContent = htmlContent.replace(/\n/g, '<br>');
    }

    return `
      <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 8px;">
        ${htmlContent}
      </div>
    `;
  };

  const formatWithAI = async (field) => {
    const text = field === 'subject' ? emailForm.subject : emailForm.message;
    if (!text.trim()) return;

    const setLoading = field === 'subject' ? setIsAiLoadingSubject : setIsAiLoadingMessage;
    setLoading(true);
    setError(null);

    try {
      const result = await adminApi.formatWithAI(text, field);

      if (result.formatted) {
        if (field === 'subject') {
          setEmailForm(prev => ({ ...prev, subject: result.formatted }));
        } else {
          setEmailForm(prev => ({ ...prev, message: result.formatted }));
        }
      }
    } catch (err) {
      console.error('AI formatting error:', err);
      setError(err.message || 'Failed to format with AI.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-primary/10">
          <SettingsIcon className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Settings</h1>
          <p className="text-text-secondary">
            Manage admin panel settings and preferences
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-danger/10 border border-danger/20 rounded-lg flex items-center gap-3 text-danger">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-3 text-success">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <p>{successMessage}</p>
          <button onClick={() => setSuccessMessage(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Tabs */}
        <div className="lg:w-56 flex-shrink-0">
          <div className="card p-2">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === id
                    ? 'bg-primary/10 text-primary'
                    : 'text-text-secondary hover:bg-bg-secondary'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          {/* Email Settings Tab */}
          {activeTab === 'email' && (
            <div className="space-y-6">
              {/* Email Config Form */}
              {showConfigForm ? (
                <div className="card p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-text-primary">
                      {editingConfig ? 'Edit Email Configuration' : 'Add Email Configuration'}
                    </h2>
                    <button
                      onClick={() => {
                        setShowConfigForm(false);
                        setEditingConfig(null);
                        setConfigForm(DEFAULT_EMAIL_CONFIG);
                      }}
                      className="p-2 hover:bg-bg-secondary rounded-lg"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* App Password Info */}
                  <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                    <h3 className="font-medium text-primary mb-2 flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Using Gmail? Set up an App Password
                    </h3>
                    <p className="text-sm text-text-secondary mb-3">
                      If you're using Gmail, you'll need to create an App Password instead of your regular password.
                    </p>
                    <ol className="text-sm text-text-secondary space-y-1 ml-4 list-decimal">
                      <li>Go to your Google Account settings</li>
                      <li>Navigate to Security → 2-Step Verification (must be enabled)</li>
                      <li>Scroll down to "App passwords"</li>
                      <li>Select "Mail" and your device, then click "Generate"</li>
                    </ol>
                    <a
                      href="https://myaccount.google.com/apppasswords"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-3 text-sm text-primary hover:underline"
                    >
                      Go to Google App Passwords
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">
                        Configuration Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        value={configForm.name}
                        onChange={(e) => setConfigForm({ ...configForm, name: e.target.value })}
                        placeholder="e.g., Support Email, Marketing Email"
                        className="input w-full"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-1">
                          SMTP Host <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          value={configForm.smtpHost}
                          onChange={(e) => setConfigForm({ ...configForm, smtpHost: e.target.value })}
                          placeholder="smtp.gmail.com"
                          className="input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-1">
                          SMTP Port
                        </label>
                        <input
                          type="text"
                          value={configForm.smtpPort}
                          onChange={(e) => setConfigForm({ ...configForm, smtpPort: e.target.value })}
                          placeholder="587"
                          className="input w-full"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">
                        SMTP Username (Email) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        value={configForm.smtpUser}
                        onChange={(e) => setConfigForm({ ...configForm, smtpUser: e.target.value })}
                        placeholder="your-email@gmail.com"
                        className="input w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">
                        SMTP Password / App Password {!editingConfig && <span className="text-danger">*</span>}
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={configForm.smtpPassword}
                          onChange={(e) => setConfigForm({ ...configForm, smtpPassword: e.target.value })}
                          placeholder={editingConfig ? 'Leave blank to keep current password' : 'Enter password or app password'}
                          className="input w-full pr-10"
                          autoComplete="new-password"
                          spellCheck="false"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                          title={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-1">
                          From Name
                        </label>
                        <input
                          type="text"
                          value={configForm.fromName}
                          onChange={(e) => setConfigForm({ ...configForm, fromName: e.target.value })}
                          placeholder="Botibonga"
                          className="input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-1">
                          From Email
                        </label>
                        <input
                          type="email"
                          value={configForm.fromEmail}
                          onChange={(e) => setConfigForm({ ...configForm, fromEmail: e.target.value })}
                          placeholder="noreply@botibonga.com"
                          className="input w-full"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowConfigForm(false);
                        setEditingConfig(null);
                        setConfigForm(DEFAULT_EMAIL_CONFIG);
                      }}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveConfig}
                      disabled={isSaving}
                      className="btn-primary"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          {editingConfig ? 'Update' : 'Save'} Configuration
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="card p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-semibold text-text-primary">
                        Email Configurations
                      </h2>
                      <p className="text-sm text-text-muted">
                        Manage your sender email addresses
                      </p>
                    </div>
                    <button onClick={handleAddConfig} className="btn-primary">
                      <Plus className="w-4 h-4" />
                      Add Configuration
                    </button>
                  </div>

                  {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : emailConfigs.length === 0 ? (
                    <div className="text-center py-12 border border-dashed border-border rounded-lg">
                      <Mail className="w-12 h-12 text-text-muted mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-text-primary mb-2">
                        No email configurations
                      </h3>
                      <p className="text-text-muted mb-4">
                        Add an email configuration to start sending emails to users.
                      </p>
                      <button onClick={handleAddConfig} className="btn-primary">
                        <Plus className="w-4 h-4" />
                        Add Your First Configuration
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {emailConfigs.map((config) => (
                        <div
                          key={config.id}
                          className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-bg-secondary/50 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <Mail className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-text-primary">{config.name}</p>
                              <p className="text-sm text-text-muted">
                                {config.fromEmail || config.smtpUser}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditConfig(config)}
                              className="p-2 hover:bg-primary/10 rounded-lg text-text-muted hover:text-primary transition-colors"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(config)}
                              className="p-2 hover:bg-danger/10 rounded-lg text-text-muted hover:text-danger transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Compose Email Tab */}
          {activeTab === 'compose' && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-6">
                Send Email to Users
              </h2>

              {isLoading ? (
                <div className="space-y-4 animate-pulse">
                  {/* Send From Skeleton */}
                  <div>
                    <div className="h-4 w-24 bg-bg-secondary rounded mb-2"></div>
                    <div className="h-12 bg-bg-secondary rounded-lg"></div>
                  </div>
                  {/* Recipients Skeleton */}
                  <div>
                    <div className="h-4 w-20 bg-bg-secondary rounded mb-2"></div>
                    <div className="flex gap-2 mb-3">
                      <div className="h-10 w-24 bg-bg-secondary rounded-lg"></div>
                      <div className="h-10 w-28 bg-bg-secondary rounded-lg"></div>
                      <div className="h-10 w-32 bg-bg-secondary rounded-lg"></div>
                    </div>
                  </div>
                  {/* Subject Skeleton */}
                  <div>
                    <div className="h-4 w-16 bg-bg-secondary rounded mb-2"></div>
                    <div className="h-12 bg-bg-secondary rounded-lg"></div>
                  </div>
                  {/* Message Skeleton */}
                  <div>
                    <div className="h-4 w-20 bg-bg-secondary rounded mb-2"></div>
                    <div className="h-48 bg-bg-secondary rounded-lg"></div>
                  </div>
                  {/* Button Skeleton */}
                  <div className="flex justify-end mt-6">
                    <div className="h-10 w-32 bg-bg-secondary rounded-lg"></div>
                  </div>
                </div>
              ) : emailConfigs.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-border rounded-lg">
                  <AlertCircle className="w-12 h-12 text-warning mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    No email configuration found
                  </h3>
                  <p className="text-text-muted mb-4">
                    Please add an email configuration in the Email Settings tab before sending emails.
                  </p>
                  <button onClick={() => setActiveTab('email')} className="btn-primary">
                    <Mail className="w-4 h-4" />
                    Go to Email Settings
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Send From */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Send From <span className="text-danger">*</span>
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setConfigDropdownOpen(!configDropdownOpen)}
                        onBlur={() => setTimeout(() => setConfigDropdownOpen(false), 150)}
                        className="input w-full text-left flex items-center justify-between"
                      >
                        <span className={selectedConfig ? 'text-text-primary' : 'text-text-muted'}>
                          {selectedConfig ? (
                            <span className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-primary" />
                              <span>
                                <span className="font-medium">{selectedConfig.name}</span>
                                <span className="text-text-muted ml-2">({selectedConfig.fromEmail || selectedConfig.smtpUser})</span>
                              </span>
                            </span>
                          ) : (
                            'Select email configuration...'
                          )}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${configDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {configDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-lg shadow-lg overflow-hidden">
                          <div className="py-1">
                            {emailConfigs.map((config) => (
                              <button
                                key={config.id}
                                type="button"
                                onClick={() => {
                                  setEmailForm({ ...emailForm, configId: config.id });
                                  setConfigDropdownOpen(false);
                                }}
                                className={`w-full px-4 py-3 text-left hover:bg-primary/5 flex items-center justify-between transition-colors ${
                                  emailForm.configId === config.id ? 'bg-primary/10' : ''
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <Mail className="w-4 h-4 text-primary" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-text-primary">{config.name}</p>
                                    <p className="text-sm text-text-muted">{config.fromEmail || config.smtpUser}</p>
                                  </div>
                                </div>
                                {emailForm.configId === config.id && (
                                  <Check className="w-4 h-4 text-primary" />
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {selectedConfig && (
                      <p className="text-sm text-text-muted mt-2">
                        Emails will be sent from: {selectedConfig.fromName} &lt;{selectedConfig.fromEmail || selectedConfig.smtpUser}&gt;
                      </p>
                    )}
                  </div>

                  {/* Recipients */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Recipients
                    </label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {['all', 'selected', 'custom'].map((type) => (
                        <button
                          key={type}
                          onClick={() => setEmailForm({ ...emailForm, recipients: type })}
                          className={`px-4 py-2 rounded-lg border transition-colors ${
                            emailForm.recipients === type
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border hover:bg-bg-secondary'
                          }`}
                        >
                          {type === 'all' && 'All Users'}
                          {type === 'selected' && 'Select Users'}
                          {type === 'custom' && 'Custom Emails'}
                        </button>
                      ))}
                    </div>

                    {emailForm.recipients === 'all' && (
                      <p className="text-sm text-text-muted">
                        Email will be sent to all {users.length} registered users.
                      </p>
                    )}

                    {emailForm.recipients === 'selected' && (
                      <div className="border border-border rounded-lg max-h-48 overflow-y-auto">
                        {isLoadingUsers ? (
                          <div className="p-4 flex items-center justify-center gap-2 text-text-muted">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>Loading users...</span>
                          </div>
                        ) : users.length === 0 ? (
                          <p className="p-4 text-center text-text-muted">No users found</p>
                        ) : (
                          users.map((user) => (
                            <label
                              key={user.id}
                              className="flex items-center gap-3 p-3 hover:bg-bg-secondary cursor-pointer border-b border-border last:border-0"
                            >
                              <input
                                type="checkbox"
                                checked={emailForm.selectedUsers.includes(user.email)}
                                onChange={() => toggleUserSelection(user.email)}
                                className="w-4 h-4 text-primary rounded border-border focus:ring-primary"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-text-primary truncate">
                                  {user.name || user.email}
                                </p>
                                <p className="text-xs text-text-muted truncate">{user.email}</p>
                              </div>
                            </label>
                          ))
                        )}
                      </div>
                    )}

                    {emailForm.recipients === 'custom' && (
                      <textarea
                        value={emailForm.customEmails}
                        onChange={(e) => setEmailForm({ ...emailForm, customEmails: e.target.value })}
                        placeholder="Enter email addresses separated by commas..."
                        className="input min-h-[100px]"
                      />
                    )}
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Subject <span className="text-danger">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={emailForm.subject}
                        onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                        placeholder="Email subject"
                        className="input w-full pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => formatWithAI('subject')}
                        disabled={!emailForm.subject.trim() || isAiLoadingSubject}
                        className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-colors ${
                          emailForm.subject.trim() && !isAiLoadingSubject
                            ? 'text-primary hover:bg-primary/10 cursor-pointer'
                            : 'text-text-muted/50 cursor-not-allowed'
                        }`}
                        title="Format with AI"
                      >
                        {isAiLoadingSubject ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-text-primary">
                        Message <span className="text-danger">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowPreview(!showPreview)}
                        className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                          showPreview
                            ? 'bg-primary text-white'
                            : 'bg-bg-secondary text-text-secondary hover:bg-primary/10 hover:text-primary'
                        }`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        {showPreview ? 'Edit' : 'Preview'}
                      </button>
                    </div>
                    <div className="border border-border rounded-lg overflow-hidden">
                      {/* Formatting Toolbar */}
                      <div className="flex items-center gap-1 p-2 bg-bg-secondary border-b border-border flex-wrap">
                        <button
                          type="button"
                          onClick={() => insertFormatting('<b>', '</b>')}
                          className="p-2 rounded hover:bg-primary/10 text-text-secondary hover:text-primary transition-colors"
                          title="Bold"
                        >
                          <Bold className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => insertFormatting('<i>', '</i>')}
                          className="p-2 rounded hover:bg-primary/10 text-text-secondary hover:text-primary transition-colors"
                          title="Italic"
                        >
                          <Italic className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => insertFormatting('<u>', '</u>')}
                          className="p-2 rounded hover:bg-primary/10 text-text-secondary hover:text-primary transition-colors"
                          title="Underline"
                        >
                          <Underline className="w-4 h-4" />
                        </button>
                        <div className="w-px h-5 bg-border mx-1" />
                        <button
                          type="button"
                          onClick={insertHeading}
                          className="p-2 rounded hover:bg-primary/10 text-text-secondary hover:text-primary transition-colors"
                          title="Heading"
                        >
                          <Heading2 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={insertLink}
                          className="p-2 rounded hover:bg-primary/10 text-text-secondary hover:text-primary transition-colors"
                          title="Insert Link"
                        >
                          <Link className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={insertList}
                          className="p-2 rounded hover:bg-primary/10 text-text-secondary hover:text-primary transition-colors"
                          title="Bullet List"
                        >
                          <List className="w-4 h-4" />
                        </button>
                        <div className="w-px h-5 bg-border mx-1" />
                        <button
                          type="button"
                          onClick={() => formatWithAI('message')}
                          disabled={!emailForm.message.trim() || isAiLoadingMessage}
                          className={`p-2 rounded flex items-center gap-1.5 transition-colors ${
                            emailForm.message.trim() && !isAiLoadingMessage
                              ? 'hover:bg-primary/10 text-text-secondary hover:text-primary cursor-pointer'
                              : 'text-text-muted/50 cursor-not-allowed'
                          }`}
                          title="Format with AI"
                        >
                          {isAiLoadingMessage ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Sparkles className="w-4 h-4" />
                          )}
                          <span className="text-xs font-medium">AI</span>
                        </button>
                        <span className="text-xs text-text-muted ml-auto hidden sm:block">
                          HTML supported • Use {'{username}'} for personalization
                        </span>
                      </div>

                      {showPreview ? (
                        <div className="p-4 min-h-[200px] bg-gray-50">
                          <div className="text-xs text-text-muted mb-3 flex items-center gap-2">
                            <Code className="w-3.5 h-3.5" />
                            HTML Preview
                          </div>
                          <div
                            className="bg-white border border-border rounded-lg p-4 shadow-sm"
                            dangerouslySetInnerHTML={{ __html: renderHtmlPreview() }}
                          />
                        </div>
                      ) : (
                        <textarea
                          ref={messageRef}
                          value={emailForm.message}
                          onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                          placeholder="Write your HTML email here...&#10;&#10;Use HTML tags like <b>bold</b>, <i>italic</i>, <u>underline</u>&#10;Use {username} to insert recipient's first name."
                          className="w-full p-3 min-h-[200px] resize-y focus:outline-none font-mono text-sm"
                        />
                      )}
                    </div>
                    <p className="text-xs text-text-muted mt-2">
                      Tip: You can write raw HTML or use the toolbar buttons to insert formatted elements.
                    </p>
                  </div>
                </div>
              )}

              {/* Send Result Message */}
              {sendResult && sendResult.sent === 0 && sendResult.failed > 0 && (
                <div className="mt-4 p-4 bg-danger/10 border border-danger/20 rounded-lg flex items-center gap-3 text-danger">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Failed to send emails</p>
                    <p className="text-sm text-danger/80">
                      {sendResult.failed} email{sendResult.failed !== 1 ? 's' : ''} failed to deliver. Please check your email configuration.
                    </p>
                  </div>
                </div>
              )}

              {sendResult && sendResult.sent > 0 && sendResult.failed > 0 && (
                <div className="mt-4 p-4 bg-warning/10 border border-warning/20 rounded-lg flex items-center gap-3 text-warning">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Partially sent</p>
                    <p className="text-sm text-warning/80">
                      {sendResult.sent} email{sendResult.sent !== 1 ? 's' : ''} delivered, {sendResult.failed} failed
                    </p>
                  </div>
                </div>
              )}

              {sendResult && sendResult.sent > 0 && sendResult.failed === 0 && (
                <div className="mt-4 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-3 text-success">
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">
                      Email{sendResult.sent > 1 ? 's' : ''} sent successfully!
                    </p>
                    <p className="text-sm text-success/80">
                      {sendResult.sent} email{sendResult.sent !== 1 ? 's' : ''} delivered
                    </p>
                  </div>
                </div>
              )}

              {emailConfigs.length > 0 && (
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleSendEmail}
                    disabled={isSending || !emailForm.configId}
                    className="btn-primary"
                  >
                    {isSending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Email
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-2">
                Notification Preferences
              </h2>
              <p className="text-sm text-text-muted mb-6">
                Email notifications coming soon
              </p>

              <div className="space-y-4 opacity-50 pointer-events-none">
                {[
                  { key: 'newUser', label: 'New User Registration', desc: 'Get notified when a new user signs up' },
                  { key: 'newFeedback', label: 'New Feedback', desc: 'Get notified when users submit feedback' },
                  { key: 'newRequest', label: 'New Custom Request', desc: 'Get notified when users submit custom agent requests' },
                  { key: 'dailyDigest', label: 'Daily Digest', desc: 'Receive a daily summary of platform activity' },
                  { key: 'weeklyReport', label: 'Weekly Report', desc: 'Receive a weekly analytics report' },
                ].map(({ key, label, desc }) => (
                  <div
                    key={key}
                    className="flex items-center justify-between p-4 border border-border rounded-lg bg-bg-secondary/30"
                  >
                    <div>
                      <p className="font-medium text-text-primary">{label}</p>
                      <p className="text-sm text-text-muted">{desc}</p>
                    </div>
                    <div className="relative">
                      <div className="w-11 h-6 rounded-full bg-gray-300" />
                      <div className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-md" />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  disabled
                  className="btn-primary opacity-50 cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  Save Preferences
                </button>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-6">
                Security Settings
              </h2>

              <div className="space-y-6">
                {/* Current Admin Info */}
                <div className="p-4 bg-bg-secondary rounded-lg">
                  <h3 className="font-medium text-text-primary mb-3">Current Admin</h3>
                  <div className="flex items-center gap-4">
                    {admin?.picture ? (
                      <img
                        src={admin.picture}
                        alt={admin.name}
                        className="w-12 h-12 rounded-full"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-lg font-medium text-primary">
                          {admin?.name?.charAt(0) || 'A'}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-text-primary">{admin?.name}</p>
                      <p className="text-sm text-text-muted">{admin?.email}</p>
                    </div>
                  </div>
                </div>

                {/* Allowed Domains */}
                <div>
                  <h3 className="font-medium text-text-primary mb-2">Allowed Admin Emails</h3>
                  <p className="text-sm text-text-muted mb-3">
                    Only these email domains and addresses can access the admin panel.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 p-3 bg-bg-secondary rounded-lg">
                      <Shield className="w-4 h-4 text-success" />
                      <span className="text-text-primary">@botibonga.com</span>
                      <span className="badge badge-success ml-auto">Domain</span>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-bg-secondary rounded-lg">
                      <Shield className="w-4 h-4 text-success" />
                      <span className="text-text-primary">@infogate-ventures.com</span>
                      <span className="badge badge-success ml-auto">Domain</span>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-bg-secondary rounded-lg">
                      <Shield className="w-4 h-4 text-primary" />
                      <span className="text-text-primary">dennismthairu@gmail.com</span>
                      <span className="badge badge-primary ml-auto">Special</span>
                    </div>
                  </div>
                </div>

                {/* Session Info */}
                <div>
                  <h3 className="font-medium text-text-primary mb-2">Session</h3>
                  <p className="text-sm text-text-muted">
                    Sessions are managed via HTTP-only cookies and expire after 24 hours of inactivity.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteConfig}
        title="Delete Email Configuration"
        message={`Are you sure you want to delete "${deleteConfirm?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}
